import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    categoryId: String,
    baseCid: String,
    popularCategories: Array
  }

  connect() {
    this.boundCleanup = this.cleanup.bind(this)
    this.boundInitialize = this.initialize.bind(this)
    this.trackingDebounce = null

    document.addEventListener('turbolinks:before-cache', this.boundCleanup)

    if (!window.tweakwiseInitialized) {
      document.addEventListener('turbolinks:load', this.boundInitialize)
      window.tweakwiseInitialized = true
    }

    this.initialize()
  }

  disconnect() {
    this.cleanup()
    this.removeEventHandlers()
    document.removeEventListener('turbolinks:before-cache', this.boundCleanup)
    document.removeEventListener('turbolinks:load', this.boundInitialize)
  }

  cleanup() {
    if (this.trackingDebounce) {
      clearTimeout(this.trackingDebounce)
    }

    document.querySelectorAll('#twn-starter-overlay').forEach(overlay => overlay.remove())

    if (window.tweakwiseListerInstance) {
      try {
        if (typeof window.tweakwiseListerInstance.destroy === 'function') {
          window.tweakwiseListerInstance.destroy()
        }
        window.tweakwiseListerInstance = null
      } catch (e) {
        console.warn('Failed to destroy Tweakwise instance', e)
      }
    }

    if (this.element) {
      this.element.innerHTML = ''
    }
  }

  initialize() {
    if (window.tweakwiseInitializing) {
      return
    }
    window.tweakwiseInitializing = true

    const urlParams = new URLSearchParams(window.location.search)
    const isSearch = urlParams.has('tn_q')

    document.querySelectorAll('#twn-starter-overlay').forEach(overlay => overlay.remove())

    if (window.tweakwiseListerInstance) {
      try {
        if (typeof window.tweakwiseListerInstance.destroy === 'function') {
          window.tweakwiseListerInstance.destroy()
        }
        window.tweakwiseListerInstance = null
      } catch (e) {
        console.warn('Failed to destroy previous Tweakwise instance', e)
      }
    }

    if (this.element) {
      this.element.innerHTML = ''
    }

    document.querySelectorAll('#twn-starter-overlay').forEach(overlay => overlay.remove())

    if (!isSearch) {
      window["twn-starter-config"].cid = this.categoryIdValue
    } else {
      window["twn-starter-config"].cid = this.baseCidValue
    }

    this.setupEventHandlers()

    setTimeout(() => {
      document.querySelectorAll('#twn-starter-overlay').forEach(overlay => overlay.remove())

      const listerConfig = {
        output: '#tweakwise-output',
        cid: isSearch ? this.baseCidValue : this.categoryIdValue,
        noResult: {
          enabled: true,
          hideDefault: true,
          customHtmlFn: (event) => {
            const el = document.createElement('div')
            el.className = 'product-empty'

            let categoriesHtml = ''
            const categories = this.popularCategoriesValue
            if (categories && categories.length > 0) {
              const cardsHtml = categories.map(cat =>
                '<a href="' + cat.link + '" class="product-empty__category-card d-flex flex-column align-items-center justify-content-center bg-white text-decoration-none">' +
                  '<img class="product-empty__category-image" src="' + cat.image + '" alt="' + cat.name + '" />' +
                  '<span class="product-empty__category-name fw-semibold">' + cat.name + '</span>' +
                '</a>'
              ).join('')

              categoriesHtml =
                '<div class="product-empty__categories">' +
                  '<h3 class="product-empty__categories-title fw-bold mb-3 mt-4">' +
                    (window.i18n ? window.i18n.t('product_empty.popular_categories') : 'Popular categories') +
                  '</h3>' +
                  '<div class="product-empty__categories-grid">' + cardsHtml + '</div>' +
                '</div>'
            }

            el.innerHTML =
              '<h2 class="product-empty__title">' + (window.i18n ? window.i18n.t('product_empty.title') : 'No results') + '</h2>' +
              '<div>' +
                '<div class="product-empty__check">' +
                  '<span>' + (window.i18n ? window.i18n.t('product_empty.text1') : 'Check the spelling of your search term') + '</span>' +
                '</div>' +
              '</div>' +
              '<a href="/" class="btn btn-primary product-empty__link">' +
                (window.i18n ? window.i18n.t('quicksearch.back_to_home') : 'Reset all filters') +
              '</a>' +
              categoriesHtml
            // Move no-results wrapper into __right after Tweakwise renders it
            setTimeout(() => {
              const wrapper = document.querySelector('.twn-starter__no-results__wrapper')
              const right = document.querySelector('.twn-starter__right')
              if (wrapper && right) {
                right.prepend(wrapper)
              }
            }, 0)
            return el
          }
        },
        on: window["twn-starter-config"].on
      }

      if (typeof tweakwiseListerPage !== 'undefined') {
        this.initializeLister(listerConfig, isSearch, urlParams)
      } else {
        const starterReadyHandler = () => {
          this.initializeLister(listerConfig, isSearch, urlParams)
          window.removeEventListener('twn.starter.ready', starterReadyHandler)
        }
        window.addEventListener('twn.starter.ready', starterReadyHandler, { once: true })
      }

      window.tweakwiseInitializing = false
    }, 100)
  }

  initializeLister(config, isSearch, urlParams) {
    if (isSearch) {
      const searchQuery = urlParams.get('tn_q')

      // Set query in config BEFORE creating instance
      config.query = searchQuery
      window["twn-starter-config"].query = searchQuery
    } else {
      // Clear any previous search query when navigating to a category page
      delete config.query
      delete window["twn-starter-config"].query
    }

    window.tweakwiseListerInstance = tweakwiseListerPage(config)
    window.tweakwiseListerInstance.setOpen(true)

    if (isSearch) {
      const searchQuery = urlParams.get('tn_q')
      // Set the query so Tweakwise knows it's in search mode
      window.tweakwiseListerInstance.setQuery(searchQuery)
    } else {
      // Clear any previous search query on the instance
      window.tweakwiseListerInstance.setQuery(null)
    }

    setTimeout(() => {
      const overlays = document.querySelectorAll('#twn-starter-overlay')
      if (overlays.length > 1) {
        console.warn('Multiple overlays detected, removing duplicates')
        overlays.forEach((overlay, index) => {
          if (index > 0) overlay.remove()
        })
      }
    }, 500)

  }

  setupEventHandlers() {
    // Register interaction handlers via Tweakwise config (add-to-cart, favorites, etc.)
    window["twn-starter-config"].on = {
      'twn.add-to-cart': (event) => this.handleAddToCart(event),
      'twn.add-to-favorites': (event) => this.handleAddToFavorites(event)
    }

    // Tweakwise lifecycle events (items.loaded, etc.) don't fire reliably via config —
    // use MutationObserver on document.body to detect when product tiles are rendered
    this.lastTrackedProductIds = null

    this.productObserver = new MutationObserver(() => {
      const overlay = document.querySelector('#twn-starter-overlay')
      if (!overlay) return

      const products = overlay.querySelectorAll('.twn-product-tile-dynamic[data-item-id]')
      if (products.length === 0) return

      // Build a fingerprint of current product IDs to avoid duplicate tracking
      const productIds = Array.from(products).map(el => el.getAttribute('data-item-id')).join(',')
      if (productIds === this.lastTrackedProductIds) return

      this.lastTrackedProductIds = productIds
      this.debouncedTrackViewItemList()
    })

    this.productObserver.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  removeEventHandlers() {
    if (this.productObserver) {
      this.productObserver.disconnect()
      this.productObserver = null
    }
  }

  handleAddToCart(event) {
    if (window.handleTweakwiseAddToCart) {
      window.handleTweakwiseAddToCart(event)
    }
  }

  handleAddToFavorites(event) {
    const item = event.data
    const productId = item.itemno ? item.itemno.split('-')[1] : null

    if (!productId) {
      console.error('No valid product ID found', item)
      return
    }

    if (window.showWishlistModal) {
      window.showWishlistModal(productId, item.title, item.image)
    }
  }

  debouncedTrackViewItemList() {
    if (this.trackingDebounce) clearTimeout(this.trackingDebounce)
    this.trackingDebounce = setTimeout(() => {
      this.trackViewItemList()
    }, 300)
  }

  trackViewItemList() {
    window.dataLayer = window.dataLayer || []

    // Tweakwise renders products into #twn-starter-overlay on document.body, not into this.element
    const root = document.querySelector('#twn-starter-overlay') || document.body
    const productElements = root.querySelectorAll('.twn-product-tile-dynamic[data-item-id]')

    const items = []
    productElements.forEach((el) => {
      const dataItemId = el.getAttribute('data-item-id')
      const match = dataItemId?.match(/^\d+-(\d+)$/)
      const productId = match ? match[1] : null
      const title = el.querySelector('[class*="title"], [class*="name"]')?.textContent?.trim()

      if (productId) {
        items.push({
          item_name: title || '',
          item_id: productId,
          item_list_name: 'Tweakwise category'
        })
      }
    })

    if (items.length === 0) {
      console.warn('[GA4] No products found for view_item_list tracking')
      return
    }

    console.log(`[GA4] Pushing view_item_list with ${items.length} items`)

    // Clear previous ecommerce data per GA4 best practice
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: { items }
    })
  }

}
